import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, FARM_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { formatMoney } from "@/lib/format";
import type { Guid, MilkTicket, MilkUnit } from "@/api/types";
import { DialogShell } from "@/components/DialogShell";
import "./Milk.css";

/**
 * ef-049-2 — the owner's Milk screen, `/farm/milk`: a ticket entry form (ticket date,
 * quantity, unit, price per unit, Paid/Not-yet-paid — mirrors `Grain.tsx`'s own delivery
 * toggle) and the ticket list. Calls ef-049-2's own routes
 * (`MilkTicketEndpoints.cs`/`MilkTicketContracts.cs`) directly —
 * `api/client.ts`'s own `// ---- ef-049-2: milk tickets ----` section. Per ADR 0009's
 * "one page template" — `PageHeader` (title + the one primary action, "Record a
 * ticket") → the Farm area tab strip → content in a card.
 *
 * NO SEPARATE DIALOG COMPONENT FILE — this job's own file scope names only
 * `Milk.tsx`/`Milk.css`, so the dialog lives here as a local, unexported component, same
 * posture `Fields.tsx` already takes for its own scope.
 *
 * NO DELETE/ARCHIVE ROUTE — `MilkTicketEndpoints.cs` ships create + list only this wave
 * (D2: tickets only). Every row is keyed by `[data-ticket-id]`, never rendered text
 * (`ticketDate` is not unique across a serial run), same discipline `Grain.tsx`'s own
 * rows take for `[data-bin-id]`/`[data-contract-id]`.
 *
 * ENTITY IS RESOLVED IMPLICITLY — the same shared, persisted switcher
 * (`useSelectedEntity`) every other Farm screen reads, never a picker inside the ticket
 * form itself.
 */

type MilkStatus = "loading" | "ready" | "error";

const UNIT_OPTIONS: { value: MilkUnit; label: string }[] = [
  { value: "gal", label: "Gallons" },
  { value: "cwt", label: "Hundredweight" },
];

export function Milk() {
  const { entityId, status: entityStatus } = useSelectedEntity();
  const [tickets, setTickets] = useState<MilkTicket[] | null>(null);
  const [status, setStatus] = useState<MilkStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  /** Re-reads the whole ticket list — same "wholesale reload after every write, never
   *  patch arrays in place" convention `Grain()`'s own `load()` documents for itself. */
  const load = useCallback(async (): Promise<void> => {
    setStatus("loading");
    setError(null);
    try {
      setTickets(await demoApi.listMilkTickets(entityId ?? undefined));
      setStatus("ready");
    } catch (err) {
      setError(problemMessage(err, "Could not load milk tickets."));
      setStatus("error");
    }
  }, [entityId]);

  useEffect(() => {
    if (entityStatus === "loading") return;
    void load();
  }, [load, entityStatus]);

  return (
    <main className="milk-page">
      <PageHeader
        title="Milk"
        subtitle="Tickets from the co-op, paid on the spot or invoiced."
        action={
          <button type="button" className="btn btn--primary" onClick={() => setDialogOpen(true)}>
            Record a ticket
          </button>
        }
      />

      <AreaTabs area="/farm" tabs={FARM_TABS} label="Farm" />

      <section
        className="milk-page__card"
        aria-labelledby="milk-tickets-heading"
        data-state={status === "ready" && (tickets?.length ?? 0) === 0 ? "empty" : status}
      >
        <h2 id="milk-tickets-heading" className="milk-page__card-title">
          Tickets
        </h2>

        <Why>
          Paid posts a balanced entry now, against this tenant's own Milk Sales income
          account. Not yet paid posts an invoice instead — no entry until it settles.
        </Why>

        {status === "loading" && <StateMessage tone="loading" title="Loading tickets…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load tickets." detail={error ?? undefined} />
        )}

        {status === "ready" && (tickets?.length ?? 0) === 0 && (
          <StateMessage
            tone="empty"
            title="No tickets yet."
            detail="Record your first milk ticket to get started."
          />
        )}

        {status === "ready" && tickets && tickets.length > 0 && (
          <ul className="milk-page__list" aria-label="Milk tickets">
            {tickets.map((t) => (
              <TicketRow key={t.id} ticket={t} />
            ))}
          </ul>
        )}
      </section>

      {dialogOpen && (
        <RecordTicketDialog
          entityId={entityId}
          onClose={() => setDialogOpen(false)}
          onRecorded={() => void load()}
        />
      )}
    </main>
  );
}

function unitLabel(unit: MilkUnit): string {
  return unit === "gal" ? "gal" : "cwt";
}

function TicketRow({ ticket }: { ticket: MilkTicket }) {
  return (
    <li className="milk-page__row" data-ticket-id={ticket.id} data-state={ticket.paid ? "paid" : "invoiced"}>
      <span className="milk-page__meta">
        <span className="milk-page__tag">{ticket.ticketDate}</span>
        <span className="milk-page__sub">
          {ticket.qty.toFixed(2)} {unitLabel(ticket.unit)} at{" "}
          <span className="milk-page__money">${ticket.pricePerUnit.toFixed(2)}</span>/{unitLabel(ticket.unit)}
        </span>
      </span>

      <span className="milk-page__settle">
        <span className="milk-page__money milk-page__amount">{formatMoney(ticket.amount)}</span>
        <span className="milk-page__sub">{ticket.paid ? "Paid" : "Not yet paid (invoice)"}</span>
      </span>
    </li>
  );
}

function RecordTicketDialog({
  entityId,
  onClose,
  onRecorded,
}: {
  entityId: Guid | null;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [ticketDate, setTicketDate] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState<MilkUnit>("gal");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [paid, setPaid] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateId = useId();
  const qtyId = useId();
  const unitId = useId();
  const priceId = useId();

  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (entityId === null) {
      setError("No farm entity found for this tenant yet.");
      return;
    }
    if (ticketDate === "") {
      setError("Enter the ticket date.");
      return;
    }
    const qtyValue = Number(qty);
    if (!Number.isFinite(qtyValue) || qtyValue <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }
    const priceValue = Number(pricePerUnit);
    if (!Number.isFinite(priceValue) || priceValue <= 0) {
      setError("Price per unit must be greater than zero.");
      return;
    }
    if (paid === null) {
      setError("Choose Paid or Not yet paid.");
      return;
    }

    setSaving(true);
    try {
      await demoApi.createMilkTicket({
        entityId,
        ticketDate,
        qty: qtyValue,
        unit,
        pricePerUnit: priceValue,
        paid,
      });
      onRecorded();
      onClose();
    } catch (err) {
      // 409 (missing Milk Sales account) lands here, inline, and changes nothing — see
      // this function's own @ui-check below.
      /* @ui-check: client-side ONLY — MilkTicketEndpoints.cs's own ValidateQty/
         ValidateUnit/ValidatePricePerUnit checks still run server-side and remain the
         actual contract. The 409 for a missing chart account is NEVER guessed at
         client-side — it is surfaced inline from the server's own response, via
         `problemMessage`. */
      setError(problemMessage(err, "Could not record this ticket."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="Record a ticket"
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "…" : "Record ticket"}
          </button>
        </>
      }
    >
      <form id={formId} className="milk-page__dialog-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <span className="milk-page__field">
          <label htmlFor={dateId}>Ticket date</label>
          <input
            id={dateId}
            type="date"
            value={ticketDate}
            onChange={(e) => setTicketDate(e.target.value)}
          />
        </span>

        <span className="milk-page__field">
          <label htmlFor={qtyId}>Quantity</label>
          <input
            id={qtyId}
            type="number"
            min="0.01"
            step="0.01"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </span>

        <span className="milk-page__field">
          <label htmlFor={unitId}>Unit</label>
          <select id={unitId} value={unit} onChange={(e) => setUnit(e.target.value as MilkUnit)}>
            {UNIT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </span>

        <span className="milk-page__field">
          <label htmlFor={priceId}>Price per unit</label>
          <input
            id={priceId}
            type="number"
            min="0.01"
            step="0.01"
            className="milk-page__money-input"
            value={pricePerUnit}
            onChange={(e) => setPricePerUnit(e.target.value)}
          />
        </span>

        <fieldset className="milk-page__paid-group">
          <legend>Payment</legend>
          <label className="milk-page__radio">
            <input type="radio" name="paid" checked={paid === true} onChange={() => setPaid(true)} />
            <span>Paid</span>
          </label>
          <label className="milk-page__radio">
            <input type="radio" name="paid" checked={paid === false} onChange={() => setPaid(false)} />
            <span>Not yet paid</span>
          </label>
        </fieldset>

        {error && (
          <p className="milk-page__dialog-error" role="alert">
            {error}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
